import { apiClient, apiDelete, apiGet, apiPost, apiPut } from './client';
import type {
  PeerTenantPage,
  Tenant,
  TenantProfileUpdateRequest,
  TenantSettings,
} from '@/types';

/**
 * Tenant self-profile endpoints. Backed by {@code TenantController}'s
 * GET and PUT {@code /tenants/me} routes. Used by ProfilePage.
 *
 * Both are authenticated and tenant-scoped server-side: the JWT alone
 * resolves which tenant is being read or written.
 */
/** Scopes a peer/party text search to a single field instead of the default OR-across-all-columns search. */
export type SearchField = 'name' | 'gst' | 'phone' | 'location';

export const tenantApi = {
  /** Fetch the current logged-in user's tenant record. */
  getMe(): Promise<Tenant> {
    return apiGet<Tenant>('/tenants/me');
  },

  /** Patch the current tenant's profile fields. Returns the saved row. */
  updateMe(body: TenantProfileUpdateRequest): Promise<Tenant> {
    return apiPut<Tenant, TenantProfileUpdateRequest>('/tenants/me', body);
  },

  /**
   * GET /tenants/peers — directory of registered tenants in the caller's
   * industry, scoped to RETAILER + WHOLESALER. Drives the All Parties
   * tab on the parties page.
   *
   * Returns a Spring Page directly (not wrapped in ApiResponse), so we
   * use the raw axios client instead of the apiGet helper.
   */
  async listPeers(
    q?: string, page = 0, size = 25, tenantType?: 'RETAILER' | 'WHOLESALER',
    opts?: { isNew?: boolean; near?: boolean; requestStatus?: string; searchField?: SearchField },
  ): Promise<PeerTenantPage> {
    const { data } = await apiClient.get<PeerTenantPage>('/tenants/peers', {
      params: {
        q: q || undefined, page, size, tenantType: tenantType || undefined,
        isNew: opts?.isNew || undefined, near: opts?.near || undefined,
        requestStatus: opts?.requestStatus || undefined,
        searchField: (q && opts?.searchField) || undefined,
      },
    });
    return data;
  },

  /**
   * GET /tenants/reps — directory of REPRESENTATIVE tenants in the
   * caller's industry. Drives the Sales Reps page.
   */
  async listReps(q?: string, page = 0, size = 25): Promise<PeerTenantPage> {
    const { data } = await apiClient.get<PeerTenantPage>('/tenants/reps', {
      params: { q: q || undefined, page, size },
    });
    return data;
  },

  // ------------------------------------------------------------------
  // Drug License + Food License certificate operations
  //
  // Each kind has three operations: getCertUrl (build the GET URL for
  // an inline preview), upload (multipart POST), remove (DELETE).
  // The download URL is built rather than fetched because the FE just
  // needs the URL string to drop into <img src> / <embed src>.
  // ------------------------------------------------------------------

  /**
   * Build the inline-preview URL for a certificate. The browser will
   * issue the GET with the JWT in the Authorization header — but since
   * <img>/<embed> tags don't send our auth header automatically, the
   * caller must fetch the file as a blob and convert to an object URL
   * (see {@link fetchCertificateBlobUrl}).
   *
   * Kept here to centralise the URL shape so the controller path lives
   * in exactly one place on the FE.
   */
  certificatePath(kind: 'dl' | 'food'): string {
    return `/tenants/me/${kind}-certificate`;
  },

  /**
   * Fetch the certificate file as a blob URL suitable for `<img>` /
   * `<embed>`. Caller is responsible for calling
   * `URL.revokeObjectURL` when done.
   */
  async fetchCertificateBlobUrl(kind: 'dl' | 'food'): Promise<string> {
    const resp = await apiClient.get(this.certificatePath(kind), {
      responseType: 'blob',
    });
    return URL.createObjectURL(resp.data as Blob);
  },

  /** Upload (or replace) a certificate. Returns the updated tenant row. */
  async uploadCertificate(kind: 'dl' | 'food', file: File): Promise<Tenant> {
    const form = new FormData();
    form.append('file', file);
    // Force axios to compute the multipart Content-Type (with the right
    // boundary) by explicitly clearing the default 'application/json'
    // header that the apiClient instance was created with.
    await apiPost<Record<string, unknown>>(this.certificatePath(kind), form, {
      headers: { 'Content-Type': undefined as unknown as string },
    });
    // Return the freshly-fetched tenant so the form can re-render with
    // the new filename without an extra round-trip in the caller.
    return this.getMe();
  },

  /** Remove an existing certificate. Returns the updated tenant row. */
  async removeCertificate(kind: 'dl' | 'food'): Promise<Tenant> {
    await apiDelete<Record<string, unknown>>(this.certificatePath(kind));
    return this.getMe();
  },

  // ------------------------------------------------------------------
  // Tenant settings — product visibility and order constraints
  // ------------------------------------------------------------------

  /** Fetch the current tenant's product and order settings. */
  getSettings(): Promise<TenantSettings> {
    return apiGet<TenantSettings>('/tenants/me/settings');
  },

  /** Persist updated settings. Returns the saved settings. */
  updateSettings(body: TenantSettings): Promise<TenantSettings> {
    return apiPut<TenantSettings, TenantSettings>('/tenants/me/settings', body);
  },
};
